// The fetching hooks come through the barrel: Vite pre-bundles it, and a deep import would load a second
// copy of the axios client that never receives the app's base URL.
import {
  getMediaFileDisplay,
  isMediaFileModerated,
  NotificationType,
  useMediaFileCharacters,
  useMediaFileDetails,
  useTranslation,
} from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import clientClipboard from "@next-platform/client-common-core/services/clipboard";
import { useEventCapabilityIsUsed } from "@next-platform/client-common-core/services/databricks";
import type { MediaFileDetailsNavigation } from "@next-platform/client-common-spicy-ui";
import { MediaFileDetails as MediaFileDetailsView } from "@next-platform/client-common-spicy-ui";
import { memo, useCallback, useMemo } from "react";
import { generatePath } from "react-router-dom";

import { ROUTES } from "@/constants";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";
import useToast from "@/hooks/useToast";

import { buildMediaFileDetailsLabels, mapMediaFileDetailsContent } from "./mapMediaFileDetailsContent";
import useMediaFileActionCapabilities from "./useMediaFileActionCapabilities";
import useMediaFileConnectionNavigation from "./useMediaFileConnectionNavigation";
import useMediaFileDownload from "./useMediaFileDownload";

type MediaFileDetailsProps = {
  readonly id: string;
  readonly navigation?: MediaFileDetailsNavigation;
  /** Drawn by the shared view below `lg`; the modal supplies both. */
  readonly onClose?: () => void;
  readonly closeLabel?: string;
  readonly placeholderClassName?: string;
  /** Whether this reader blurs adult images; the NSFW chip follows the file regardless. Defaults to blurring. */
  readonly shouldBlurNsfw?: boolean;
  /** A verdict the opening surface already made, which wins over the per-file rule. Absent for the gallery and links. */
  readonly isBlurredByOpener?: boolean;
  readonly "data-testid"?: string;
};

const MediaFileDetails = ({
  id,
  navigation,
  onClose,
  closeLabel,
  placeholderClassName,
  shouldBlurNsfw = true,
  isBlurredByOpener,
  "data-testid": testId = "MediaFileDetails",
}: MediaFileDetailsProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const { displayToast } = useToast();
  const localizePath = useLocalizedPathname();
  const raiseEventCapabilityIsUsed = useEventCapabilityIsUsed();
  const isDownloadAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.MediaDownload);
  const isGenerationDetailsAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.MediaViewGenerationDetails);
  // The remaining actions have no handler yet — Keep lands in Milestone 2, the rest in Milestone 4 — so their
  // capability alone decides whether the row is offered at all. Once a handler exists, pass it through the
  // same way `onDownload` is gated below, and the row becomes usable wherever the brand allows it.
  const actionCapabilities = useMediaFileActionCapabilities();
  const details = useMediaFileDetails({ id });
  const display = useMemo(() => {
    if (details.status === "not-found") {
      return getMediaFileDisplay({ file: null, translate: t });
    }
    if (details.status !== "ready") {
      return null;
    }
    return getMediaFileDisplay({ file: details.file, translate: t });
  }, [details, t]);
  const file = display?.file ?? undefined;
  const placeholder = display?.placeholder;
  const { primaryCharacter, connections } = useMediaFileCharacters({ file, locale });
  // A surface that already decided keeps its verdict: a chat bubble judged the image on what it knows about
  // the chat, and the modal is a closer look at that same image rather than a fresh context.
  //
  // Only the fallback consults the file, and it waits for someone to have judged it: an unexamined file reads
  // `is_nsfw: false` because nobody looked, so hiding it on that flag alone would claim a confidence the data
  // does not carry.
  const isBlurred = isBlurredByOpener ?? (Boolean(file?.is_nsfw) && Boolean(file && isMediaFileModerated(file)) && shouldBlurNsfw);

  const labels = useMemo(() => buildMediaFileDetailsLabels(t), [t]);
  const loadStatus = details.status === "idle" ? "loading" : details.status;
  const status = placeholder ? "unavailable" : loadStatus;

  const content = useMemo(() => {
    if (!file) {
      return undefined;
    }
    const mapped = mapMediaFileDetailsContent({ file, translate: t, locale, primaryCharacter, connections, isBlurred, localizePath });
    // The prompt is what turns the sidebar section into "Generation details"; a brand without the capability
    // gets the rest of the file's metadata but not this, so the mapper's own logic stays the same either way.
    return isGenerationDetailsAllowed ? mapped : { ...mapped, prompt: undefined };
  }, [file, t, locale, primaryCharacter, connections, isBlurred, localizePath, isGenerationDetailsAllowed]);

  const handleCopyLink = useCallback(() => {
    const pathname = localizePath(generatePath(ROUTES.mediaFile, { id: encodeURIComponent(id) }));
    const fileUrl = new URL(pathname, window.location.origin).href;
    clientClipboard
      .writeText(fileUrl)
      .then(() => {
        // The address is the media page, so a copied link is that page reached; a failed copy counts nothing.
        raiseEventCapabilityIsUsed(SubCapabilityKey.MediaMediaPage);
        displayToast({ message: t("gallery:mediaFilePreview.linkCopied"), type: NotificationType.success });
      })
      .catch(() => displayToast({ message: t("gallery:mediaFilePreview.copyLinkFailed"), type: NotificationType.error }));
  }, [displayToast, id, localizePath, raiseEventCapabilityIsUsed, t]);

  const download = useMediaFileDownload({ id, file });

  const handleRetry = useCallback(() => {
    if (details.status === "error") {
      details.retry();
    }
  }, [details]);

  const handleOpenConnection = useMediaFileConnectionNavigation({ primaryCharacter, connections });
  // Every activation of a character link, including the ones the browser opens in a new tab, is one usage.
  const handleActivateConnection = useCallback(() => raiseEventCapabilityIsUsed(SubCapabilityKey.MediaRelatedEntity), [raiseEventCapabilityIsUsed]);

  return (
    <MediaFileDetailsView
      status={status}
      labels={labels}
      content={content}
      placeholder={placeholder ?? undefined}
      onCopyLink={handleCopyLink}
      onDownload={isDownloadAllowed ? download.onDownload : undefined}
      actionsAllowed={actionCapabilities}
      isDownloading={download.isDownloading}
      downloadFallbackUrl={download.fallbackUrl}
      onOpenDownloadFallback={download.onOpenFallback}
      onRetry={handleRetry}
      onOpenConnection={handleOpenConnection}
      onActivateConnection={handleActivateConnection}
      navigation={navigation}
      onClose={onClose}
      closeLabel={closeLabel}
      placeholderClassName={placeholderClassName}
      data-testid={testId}
    />
  );
};

export default memo(MediaFileDetails);
